export const INVESTIGATION_STATUSES = [
  'running',
  'waiting_you',
  'waiting_other',
  'concluded',
  'closed',
  'declined',
  'ask_idle',
] as const;

export type InvestigationStatus = (typeof INVESTIGATION_STATUSES)[number];

/**
 * Ordered by what a user reaches for, with `Open` first because it is the
 * default. Closing an investigation is the only thing that takes a row out of
 * that view — before this it moved nothing, which is a large part of why the
 * customer who prompted all this never closed anything.
 */
export const LIST_FILTERS = ['Open', 'Questions', 'Concluded', 'Closed', 'All'] as const;

export type ListFilter = (typeof LIST_FILTERS)[number];

export type EvidenceKind = 'code' | 'log' | 'ticket' | 'file' | 'pr' | 'decision';

export interface EvidenceItem {
  id: string;
  kind: EvidenceKind;
  label: string;
  detail: string;
  excerpt?: string;
}

export type PlanStepStatus = 'pending' | 'active' | 'done';

export interface PlanStep {
  id: string;
  label: string;
  status: PlanStepStatus;
}

export interface ClarOption {
  value: string;
  title: string;
  sub: string;
  recommended?: boolean;
}

export interface InvestigationVerdict {
  outcome: string;
  confidence: string;
  affected: string;
  mechanism: string;
  /** Evidence the mechanism rests on — the highest-stakes row on the surface
   * had no citations at all; this is what a user forwards or pastes into a
   * ticket, so it is the one place a citation matters most. */
  cites?: string[];
  trace: string[];
  checked: string[];
  notChecked: string[];
}

export type MessageType = 'agent' | 'user' | 'plan' | 'clar' | 'steer' | 'verdict' | 'declined';

/**
 * The persisted per-row status from the server's `message_meta`. A trailing
 * `error`/`partial` reply is the only server-side trace a failed run leaves,
 * so it is how "the last run didn't finish" survives a refresh once the
 * ephemeral `runError` banner (set live from a `RUN_ERROR` event) is gone.
 */
export type MessageStatus = 'complete' | 'partial' | 'error';

export type InvestigationAttachmentKind = 'image' | 'pdf' | 'spreadsheet' | 'csv' | 'text' | 'markdown';

/** One file on a user message. Never the bytes: the agent reads those server-side. */
export interface InvestigationAttachment {
  id: string;
  filename: string;
  contentType: string;
  kind: InvestigationAttachmentKind;
  sizeBytes: number;
  pageCount?: number;
  sheetNames?: string[];
  /** A short-lived signed link, images only, refreshed by every conversation read. */
  url?: string;
  /** When `url` stops working, ISO 8601. */
  urlExpiresAt?: string;
}

export interface InvestigationMessage {
  id: string;
  type: MessageType;
  /** Absent on live-streamed rows; only persist snapshots carry it. */
  status?: MessageStatus;
  time?: string;
  text?: string;
  /** Files sent with this message; a message may carry these and no text. */
  attachments?: InvestigationAttachment[];
  /**
   * Bytes of `text-delta` already folded into this row. Persist snapshots can
   * already contain the same body a late stream replay will send again;
   * tracking how much of the stream we have accounted for lets those deltas
   * no-op instead of appending a duplicate.
   */
  streamedLength?: number;
  label?: string;
  /**
   * An agent message that went on to call a tool: the agent was narrating its
   * way to the answer, not giving it. Only ever set to true — absent means
   * final, or not yet known while the prose is still streaming.
   */
  intermediate?: boolean;
  cites?: string[];
  reasoning?: string[];
  title?: string;
  steps?: PlanStep[];
  question?: string;
  owner?: string;
  routedTo?: string;
  options?: ClarOption[];
  answered?: boolean;
  answer?: string;
  reason?: string;
  resembles?: string;
}

/**
 * The choices the UI offers, and the exact keys sent as the wire `rating`.
 *
 * The server enumerates nothing and stores whatever key it is sent, so this
 * list is the only definition of the vocabulary. Reword a label freely; change
 * a key and the rows already written no longer line up with the rows written
 * after, and no backfill can tell them apart.
 */
export const FEEDBACK_CATEGORIES = ['right', 'partially_right', 'wrong', 'unused'] as const;

export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number];

export interface InvestigationFeedback {
  id: string;
  /**
   * The key as stored, deliberately not narrowed to `FeedbackCategory`: the
   * server holds no vocabulary, so a build that has since dropped or renamed a
   * choice still has to render rows written under the old one.
   */
  rating: string;
  /** Absent rather than '' when the user typed nothing. */
  comment?: string;
  submittedAt: string;
}

/**
 * Which audience a draft is written for, in menu and chip order — the
 * customer reply is last because it is the guarded one. These are the wire's
 * own values (`post_report_view.value.view`), so the adapter narrows and
 * passes them through rather than translating.
 */
export const REPORT_VIEWS = ['internal_note', 'engineering_handoff', 'customer_reply'] as const;

export type ReportView = (typeof REPORT_VIEWS)[number];

/**
 * The one draft the panel holds. A new one replaces it whatever its view, and
 * it is never persisted — the server keeps no copy, so a reload starts empty.
 */
export interface ReportDraft {
  view: ReportView;
  content: string;
  receivedAt: string;
  supersededByReport: boolean;
}

/** The wire's own values (`fork_status`), passed through by the adapter. */
export const FORK_STATUSES = ['available', 'in_progress', 'unavailable'] as const;

export type ForkStatus = (typeof FORK_STATUSES)[number];

/**
 * Present only on an investigation Driver ran on its own from a ticket.
 * Nobody owns it: every member of the organization reads the same copy, and a
 * reply continues in the sender's private fork rather than here.
 */
export interface AutomaticTriage {
  /** Whether a reply from this reader can be taken, and if not, whether it ever will. */
  forkStatus: ForkStatus;
  /** This reader's own fork, once they have replied. */
  callerForkId?: string;
}

/** Where an investigation came from when a ticket started it, on the shared copy and on every fork of it. */
export interface InvestigationOrigin {
  ticketKey: string;
  startedAt?: string;
}

/** The wire's own values (`complexity.size`), passed through by the adapter. */
export const COMPLEXITY_SIZES = ['small', 'medium', 'large'] as const;

export type ComplexitySize = (typeof COMPLEXITY_SIZES)[number];

/** Driver's latest estimate of the support effort the ticket needs; absent until scored. */
export interface TicketComplexity {
  size: ComplexitySize;
  reason: string;
  assessedAt: string;
}

export interface Investigation {
  id: string;
  /** The ticket this investigation is about, when it has one. */
  key: string | null;
  title: string;
  status: InvestigationStatus;
  continuable?: boolean;
  time: string;
  /** One line of the newest message, for list rows. Absent outside the list. */
  preview?: string;
  who?: string;
  working?: string;
  evidence: EvidenceItem[];
  messages: InvestigationMessage[];
  pkg?: InvestigationVerdict;
  disposition?: string;
  /** Every submission on this investigation, oldest first. Feedback is
   * repeatable by design — mid-run, at the end, and again after re-opening. */
  feedback?: InvestigationFeedback[];
  runError?: {
    code: string;
    message: string;
  };
  /**
   * The agent's report, whole. Every delivery replaces it and none is ever
   * rolled back; absent means nothing has been saved yet.
   */
  report?: string;
  /**
   * The server's record of when the report was stored, carried on both the
   * delivery frame and the detail GET — never a client clock.
   */
  reportUpdatedAt?: string;
  reportDraft?: ReportDraft;
  automaticTriage?: AutomaticTriage;
  origin?: InvestigationOrigin;
  complexity?: TicketComplexity;
}
